"use client";

import React from "react";
import { WcChip, WcStack, WcTooltip, WcTypography } from "@bpipartners/webui";
import { useTheme } from "@guberno/utils/theme";
import { useTranslations } from "next-intl";
import { LuShare2 } from "react-icons/lu";

export interface CircleRefData {
  name?: string | null;
  color?: string | null;
}

interface CircleBadgesProps {
  /** Row's visible circle details. Null may also mean hidden details for a real owner. */
  circleOwner?: CircleRefData | null;
  circleIDShared?: string[] | null;
  /** Tooltip entries (lazy-loaded by `CircleBadgesLazy`, D13). */
  sharedCircles?: Array<{ name: string; color?: string | null }>;
  /** Fires when the `+N` badge is hovered — the pure component stays Apollo-free. */
  onSharedHover?: () => void;
  /**
   * "sharedIn" (circle home): the row is shared INTO the viewed circle from the
   * owning circle — the badge gets the shared treatment: outlined chip, share
   * icon instead of the plain color dot, and a "shared from circle X" tooltip.
   * Default ("default"): provenance label only (landing, grids, planning, …).
   */
  variant?: "default" | "sharedIn";
}

/**
 * Pure display component (no data fetching, no Apollo — D2).
 * Owner name + color come from the row's `circleOwner` field (D13); shared
 * names + colors are passed in (lazy-loaded by `CircleBadgesLazy`).
 */
export const CircleBadges = ({
  circleOwner,
  circleIDShared,
  sharedCircles,
  onSharedHover,
  variant = "default",
}: CircleBadgesProps) => {
  const theme = useTheme();
  const t = useTranslations();

  // Unavailable details do not establish ownership scope; omit the badge rather than guessing.
  if (!circleOwner || !circleOwner.name) {
    return null;
  }

  const ownerColor = circleOwner.color || theme.appColors.outline;
  const sharedCount = (circleIDShared ?? []).length;

  const isSharedIn = variant === "sharedIn";

  const ownerPill = (
    <WcChip
      variant={isSharedIn ? "outlined" : "filled"}
      sx={
        isSharedIn
          ? {
              backgroundColor: "transparent",
              borderColor: `${ownerColor}80`,
              color: theme.appColors.onSurface,
              fontWeight: 600,
              fontSize: "12px",
              height: "24px",
              gap: 1,
            }
          : {
              backgroundColor: `${ownerColor}20`,
              color: theme.appColors.onSurface,
              fontWeight: 600,
              fontSize: "12px",
              height: "24px",
              gap: 1,
            }
      }
      icon={
        isSharedIn ? (
          <LuShare2 size={12} style={{ color: ownerColor, flexShrink: 0 }} />
        ) : (
          <WcStack sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: ownerColor }} />
        )
      }
      label={circleOwner.name ?? ""}
    />
  );

  // Shared-in rows explain themselves on hover (option 2: icon + tooltip +
  // outlined styling), so the foreign circle name reads as provenance, not noise.
  const ownerPillShown = isSharedIn ? (
    <WcTooltip title={t("labels.sharedFromCircle", { name: circleOwner.name ?? "" })} arrow>
      {ownerPill}
    </WcTooltip>
  ) : (
    ownerPill
  );

  if (sharedCount === 0) {
    return ownerPillShown;
  }

  const sharedTooltip =
    sharedCircles && sharedCircles.length > 0 ? (
      <WcStack spacing={0.5}>
        {sharedCircles.map((circle, index) => (
          <WcStack key={`${circle.name}-${index}`} direction="row" spacing={1} alignItems="center">
            <WcStack
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: circle.color || theme.appColors.outline,
                flexShrink: 0,
              }}
            />
            <WcTypography sx={{ fontSize: "12px" }}>{circle.name}</WcTypography>
          </WcStack>
        ))}
      </WcStack>
    ) : undefined;

  return (
    <WcStack direction="row" spacing={0.5} alignItems="center">
      {ownerPillShown}
      <WcTooltip title={sharedTooltip} arrow>
        <WcChip
          sx={{
            backgroundColor: theme.appColors.surfaceContainer,
            color: theme.appColors.onSurface,
            fontWeight: 600,
            fontSize: "12px",
            height: "24px",
          }}
          label={`+${sharedCount}`}
          onMouseEnter={onSharedHover}
        />
      </WcTooltip>
    </WcStack>
  );
};

export default CircleBadges;
